import { Box } from "@mui/material";
import { Card, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { getBackendUrl } from "../../../util/envFn";
import { Controller, useForm } from "react-hook-form";
import { Button } from "@mui/material";
import { formatDate } from "../../../util/date";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { JobPost } from "../types";
import ErrorBox from "../../../components/ErrorBox";
import { JobFormInputs } from "../AdminPanel";
import { jobFormSchema } from "../AdminPanel";
import CustomMDEditor from "../../../components/CustomMD/CustomMDEditor";
import api from "../../../api";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CustomMDPreview from "../../../components/CustomMD/CustomMDPreview";

const AdminPanelJobPostCard = ({ job, index, fetchJobs }: { job: JobPost, index: number, fetchJobs: () => void }) => {
    const handleDeleteJob = (index: number) => {
        api.delete(getBackendUrl("jobs/" + job._id), {
        })
            .then(res => {
                fetchJobs();
                enqueueSnackbar("Job deleted successfully", { variant: "success" });
            })
            .catch(err => {
                enqueueSnackbar("Failed to delete job", { variant: "error" });
            });
    };

    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<JobFormInputs>({
        resolver: zodResolver(jobFormSchema),
        defaultValues: {
            jobTitle: job.title,
            pmEmail: job.pmEmail,
            jobDescription: job.description
        }
    });

    const onSubmitEdit = async (data: JobFormInputs) => {
        try {
            await api.patch(getBackendUrl("jobs/" + job._id), {
                title: data.jobTitle,
                description: data.jobDescription,
                pmEmail: data.pmEmail
            });

            fetchJobs();
            setIsEditing(false);
            setError(null);
            enqueueSnackbar("Job updated successfully", { variant: "success" });
        } catch (err) {
            setError((err as AxiosError<{ message?: string }>)?.response?.data?.message || "Failed to update job");
        }
    };

    // Cancel editing and reset form
    const handleCancelEdit = () => {
        reset({
            jobTitle: job.title,
            pmEmail: job.pmEmail,
            jobDescription: job.description
        });
        setIsEditing(false);
        setError(null);
    };

    return (
        <Card
            sx={{
                // px: 2,
                py: 1,
                border: theme => isEditing ? `3px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                // boxShadow: theme => isEditing ? `inset 0px 0px 8px 0px ${theme.palette.primary.main}` : 'none',
                boxShadow: 'none',
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: expanded || isEditing ? "background.paper" : 'rgba(0, 0, 0, 0.01)'
                },
            }}
            onClick={handleExpandClick}
        >
            <CardContent sx={{ p: 0, m: 0, pb: "0px !important", px: 2, overflow: "auto", }}>
                {isEditing ? (
                    <form onSubmit={handleSubmit(onSubmitEdit)}>
                        <Typography variant="h6" component="h2" mb={2}>Editing Job Post</Typography>
                        <Grid container spacing={2} pt={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="jobTitle"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Job Title"
                                            error={!!errors.jobTitle}
                                            helperText={errors.jobTitle?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="pmEmail"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="PM Email"
                                            type="email"
                                            error={!!errors.pmEmail}
                                            helperText={errors.pmEmail?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="jobDescription"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomMDEditor
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.jobDescription?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <ErrorBox error={error} m={2} />

                            <Box width={1} p={2} pr={0} display="flex" justifyContent="space-between" >
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    Created at: <Typography component="span" variant='body2' noWrap color="text.secondary" sx={{ fontWeight: 'bold' }}>{formatDate(job.createdAt.toString())}</Typography>
                                </Typography>
                                <Stack direction="row" gap={1}>
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={handleCancelEdit}
                                        sx={{ borderRadius: 1 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        size="small"
                                        type="submit"
                                        disabled={isSubmitting}
                                        sx={{ borderRadius: 1 }}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                    </form>
                ) : (
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Typography variant="h6">
                                {job.title}
                            </Typography>
                            <IconButton
                                aria-expanded={expanded}
                                aria-label="show more"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the parent onClick
                                    handleExpandClick();
                                }}
                            >
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Grid>

                        <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%', maxWidth: '100%' }}>
                            <Grid container spacing={4} sx={{ width: '100%', maxWidth: '100%' }}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" gutterBottom >PM Email: </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            p: 1,
                                        }}
                                    >
                                        {job.pmEmail}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" gutterBottom>Description: </Typography>
                                    <CustomMDPreview
                                        description={job.description}
                                        customStyles={{
                                            display: 'block',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} width={1} display="flex" justifyContent="space-between" >
                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            Created at: <Typography component="span" variant='body2' noWrap color="text.secondary" sx={{ fontWeight: 'bold' }}>{formatDate(job.createdAt.toString())}</Typography>
                                        </Typography>
                                        <Stack direction="row" gap={1}>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                sx={{ borderRadius: 1 }}
                                                onClick={() => setIsEditing(true)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteJob(index)}
                                                sx={{ borderRadius: 1 }}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Grid >
                            </Grid>
                        </Collapse>
                    </Grid>
                )}
            </CardContent>
        </Card>
    )
}

export default AdminPanelJobPostCard;   
